import React from 'react';
import { useTranslate } from 'react-admin';
import { Box, CircularProgress, Button, Typography, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import CheckmarkSmallIcon from '@/assets/icons/checkmark-small-icon.svg?react';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import { Palette } from '@/themes/themeOverrides/Palette';
import { globalStyles } from '@/themes/commonStyles/global.js';
import { tagPickerStyles } from '@/themes/commonStyles/tagPicker.js';
import { useTagNameSource } from '@/i18n/hooks.js';

const StyledPopup = styled(Box)(({ theme }) => ({
    '& .actions': tagPickerStyles.popupActions(theme),
    '& .loading': {
        ...globalStyles.filterLoading,
    },
}));

export const TagPickerPopup = ({
    open,
    isLoading,
    tags,
    groupedTags,
    tempSelectedIds,
    selectedIds,
    setOpen,
    setTempSelectedIds,
    handleTagToggle,
    handleToggleAll,
    handleApply,
    popupRef,
    categoriesClassName,
    containerClassName,
    actionsDisabled,
}) => {
    const translate = useTranslate();
    const nameSource = useTagNameSource();

    let popupBody;
    if (isLoading) {
        popupBody = (
            <Box className='loading'>
                <CircularProgress /> {translate('resources.device.labels.downloadingLogs')}
            </Box>
        );
    } else if (tags?.length === 0) {
        popupBody = <Box sx={{ margin: '20px', ...globalStyles.filterLoading, background: Palette.white }}>{translate('common.noResults')}</Box>;
    } else {
        popupBody = (
            <>
                <Box className={categoriesClassName}>
                    {groupedTags.map(category => (
                        <Box key={category.id}>
                            <Typography variant='body1'>{category[nameSource]}</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {category.tags.map(tag => (
                                    <Chip
                                        key={tag.id}
                                        label={tag[nameSource]}
                                        icon={<CheckmarkSmallIcon style={{ opacity: tempSelectedIds.includes(tag.id) ? 1 : 0.12 }} />}
                                        size='small'
                                        onClick={() => handleTagToggle(tag.id)}
                                        style={{
                                            backgroundColor: tempSelectedIds.includes(tag.id) ? category.color.dark : category.color.light,
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
                <Box className='actions'>
                    <Button
                        variant='contained'
                        color='secondary'
                        onClick={() => {
                            setTempSelectedIds(selectedIds);
                            setOpen(false);
                        }}
                        startIcon={<ArrowLeftIcon />}
                        className='close-popup'
                    />
                    <Button variant='contained' color='secondary' onClick={handleToggleAll}>
                        {tempSelectedIds.length === 0 ? translate('resources.product.labels.selectAll') : translate('resources.product.labels.unselectAll')}
                    </Button>
                    <Button variant='contained' color='primary' onClick={handleApply} disabled={actionsDisabled}>
                        {translate('common.apply')}
                    </Button>
                </Box>
            </>
        );
    }

    return (
        open && (
            <StyledPopup className={`${containerClassName} open`} ref={popupRef}>
                {popupBody}
            </StyledPopup>
        )
    );
};

TagPickerPopup.propTypes = {
    open: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool,
    tags: PropTypes.array,
    groupedTags: PropTypes.array.isRequired,
    tempSelectedIds: PropTypes.array.isRequired,
    selectedIds: PropTypes.array.isRequired,
    setOpen: PropTypes.func.isRequired,
    setTempSelectedIds: PropTypes.func.isRequired,
    handleTagToggle: PropTypes.func.isRequired,
    handleToggleAll: PropTypes.func.isRequired,
    handleApply: PropTypes.func.isRequired,
    popupRef: PropTypes.object,
    categoriesClassName: PropTypes.string.isRequired,
    containerClassName: PropTypes.string.isRequired,
    actionsDisabled: PropTypes.bool,
};

export default TagPickerPopup;
